import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { strategicKpisApi } from '../../services/strategicKpisApi';
import type { ActualsResponse, EvidenceResponse, KpiCatalogueEntry, KpiSelection } from '../../types/strategicKpis';
import type { DrillNode } from '../../utils/strategicKpiUrl';
import { kpiLabel, windowText } from '../../utils/strategicKpiFormat';
import { selectionKey } from '../../utils/strategicKpiSelection';
import {
    EVIDENCE_PAGE, columnLabel, evidenceCellText, evidenceDescription, evidenceKpiOptions, evidenceQuery, isEvidenceScope,
    isEvidenceUrlColumn, nextEvidenceSort, numericEvidenceColumns, orderColumns, resolveEvidenceNode, rowsAvailability,
    sortEvidenceRows, summaryLabel, summaryValue, type EvidenceSort,
} from '../../utils/strategicKpiEvidence';
import { formatDate } from '../../utils/format';
import { useWrapTableOffset } from './useTableOffset';

interface Props {
    /** The node the address names (`parseSkpiUrl(...).drill`): kpi, scope type, scope key. */
    node: DrillNode;
    /** The actuals payload, for the label, the `partial` flag and the KPI list; null while it loads (after a reload of the address). */
    data: ActualsResponse | null;
    year: number;
    asOf: string;
    /** K3: the dashboard's BUs and selection, sent along so the rows are those of the selected persons. */
    bus: string[];
    selection: KpiSelection | null;
    /** The tab the view replaced ("Dashboard", "People"): first crumb and the name of the Back button. */
    originLabel: string;
    /** Leaves the view. The page should write the address without the drill params (`withDrill(prev, null)`), not rely on history length: a reloaded drill address has no entry to go back to. */
    onBack: () => void;
    /** The KPI was switched inside the node: the page replaces `dkpi` in the address. */
    onKpiChange: (kpiKey: string) => void;
}

const LABEL_SIZE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)' } as const;
const WIDE = new Set(['DealName', 'CustomerName', 'Title', 'ProductName', 'Person', 'SalesManagerName', 'CompanyName', 'SupplierName', 'LineName', 'Name']);

/**
 * The rows behind ONE number, in the full width of the tab (masterplan skpi-redesign, E9, Q8, Q10; package B2):
 * the view the side panel's «Show the rows» opens. It replaces the tab content and has an address of its own
 * (`rows=1&dkpi&dscope&dkey`), so Back and a reload land here again.
 *
 * It asks the evidence endpoint exactly as the evidence modal does (`evidenceQuery`: kpi, year, as-of, scope,
 * the dashboard's BUs and selection) and shows what comes back: deals, meetings, sales lines, BAB lines or
 * market-data records, 500 at a time (sorting covers all of them), the HubSpot link where there is one, the
 * description and the summary of the rows. The KPI can be switched inside the node, so a person's evidence needs
 * no round trip through the panel. BAB has no person level: where the engine would answer 400 the KPI is not
 * offered, and a TOTAL node (several BUs) has no rows at all — said, not hidden.
 *
 * The page keeps the address and the data; this view owns the request and the table. Column widths are fixed
 * per kind of column (no drag handle, and nothing is stored in the browser: the modal's width key is retired
 * with it, B).
 */
export function EvidenceDrill({ node: ref, data, year, asOf, bus, selection, originLabel, onBack, onKpiChange }: Props) {
    const node = useMemo(
        () => resolveEvidenceNode(data, ref),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, ref.kpiKey, ref.scopeType, ref.scopeKey],
    );
    const [resp, setResp] = useState<EvidenceResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const root = useRef<HTMLElement>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const wrap = useRef<HTMLDivElement>(null);

    const scope = node.scopeType;
    const busKey = bus.join(',');
    const selKey = selection ? selectionKey(selection) : '';
    const kpi: KpiCatalogueEntry | undefined = data?.kpis.find(k => k.kpiKey === node.kpiKey);
    // Once the payload is there, a KPI without rows in this node (BAB below company level, a derived KPI) is not requested.
    const noRows = kpi ? rowsAvailability(kpi, node) : { ok: true as const };
    const rowsOk = noRows.ok;

    useEffect(() => {
        if (!isEvidenceScope(scope) || !rowsOk) { setResp(null); setLoading(false); setError(null); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setResp(null);
        strategicKpisApi.evidence(evidenceQuery({ kpiKey: node.kpiKey, scopeType: scope, scopeKey: node.scopeKey }, { year, asOf, bus, selection }))
            .then(r => { if (!cancelled) setResp(r); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the evidence'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // bus and selection are in the dependencies by their content, not by identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.kpiKey, scope, node.scopeKey, year, asOf, busKey, selKey, rowsOk]);

    // The view replaces the tab: focus goes to its heading once.
    useEffect(() => { heading.current?.focus({ preventScroll: true }); }, []);

    const options = useMemo(() => {
        if (!data) return [];
        const list = evidenceKpiOptions(data.kpis, node);
        return kpi && !list.includes(kpi) ? [kpi, ...list] : list;
    }, [data, node, kpi]);
    const name = kpi ? kpiLabel(kpi) : node.kpiKey;
    const hasTable = !!resp && !loading && resp.rows.length > 0;
    const offset = useWrapTableOffset(root, wrap, hasTable);

    return (
        <section ref={root} className="flex min-w-0 flex-col gap-4" aria-label={`Rows behind ${name}`} data-testid="skpi-evidence-drill">
            <div className="page-top">
                <nav className="crumbs" aria-label="Breadcrumb">
                    <ol>
                        <li><button type="button" onClick={onBack}>{originLabel}</button><ChevronRight className="i sm sep" aria-hidden="true" /></li>
                        <li><span>{node.label}</span><ChevronRight className="i sm sep" aria-hidden="true" /></li>
                        <li><span aria-current="page">{name} · rows</span></li>
                    </ol>
                </nav>
                <div className="page-actions">
                    <button type="button" className="btn secondary sm" onClick={onBack} data-testid="skpi-drill-back">
                        <ArrowLeft className="i sm" aria-hidden="true" /> Back to {originLabel}
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-head">
                    <h2 ref={heading} tabIndex={-1}>Rows behind {name}</h2>
                    <p className="sub">
                        {node.label}
                        {kpi && resp && <> · <span title={resp.window.text}>{windowText(kpi, resp.window)}</span></>}
                        {' '}· as of {formatDate(asOf)}
                    </p>
                    {options.length > 0 && (
                        <div className="end">
                            <label htmlFor="skpi-drill-kpi" className="muted" style={LABEL_SIZE}>KPI</label>
                            <select
                                id="skpi-drill-kpi"
                                className="input"
                                style={{ width: 'auto', maxWidth: 300 }}
                                value={node.kpiKey}
                                onChange={e => onKpiChange(e.target.value)}
                            >
                                {options.map(k => <option key={k.kpiKey} value={k.kpiKey}>{kpiLabel(k)}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                {resp && (
                    <div className="flex flex-col gap-2" style={LABEL_SIZE}>
                        <p className="max-w-prose" title={resp.description}>{evidenceDescription(resp.kpiKey, kpi?.category ?? '', resp.description)}</p>
                        {resp.partial && <p>Filtered: only the persons of the current selection{resp.scopeType === 'SELECTION' ? '' : ' in this node'}. Sales lines follow the persons (their sales-manager codes), not the customer company.</p>}
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {Object.entries(resp.summary).map(([k, v]) => (
                                <span key={k}><span className="muted">{summaryLabel(k)}:</span> <span className="num strong">{summaryValue(k, v, kpi?.unit)}</span></span>
                            ))}
                            {!('rows' in resp.summary) && <span><span className="muted">rows:</span> <span className="num strong">{resp.total.toLocaleString('de-CH')}</span></span>}
                            {resp.truncated && <span className="attn">showing the first {resp.rows.length.toLocaleString('de-CH')} rows only</span>}
                        </div>
                    </div>
                )}
            </div>

            {!isEvidenceScope(scope) && (
                <div className="banner attention" role="status">
                    <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                    <p>Rows open for one BU, company, team or person, not for the sum of several BUs. Go back and pick a part of the total.</p>
                </div>
            )}
            {isEvidenceScope(scope) && !noRows.ok && (
                <div className="banner attention" role="status">
                    <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                    <p>{noRows.reason}</p>
                </div>
            )}
            {loading && (
                <div className="empty" role="status">
                    <span className="ic"><Loader2 className="i animate-spin motion-reduce:animate-none" aria-hidden="true" /></span>
                    <p>Loading the rows…</p>
                </div>
            )}
            {error && (
                <div className="banner error" role="alert">
                    <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                    <p>{error}</p>
                </div>
            )}
            {resp && !loading && resp.rows.length === 0 && (
                <div className="empty"><p>No rows in this window.</p></div>
            )}
            {resp && !loading && resp.rows.length > 0 && (
                <EvidenceRows key={`${resp.kpiKey}|${resp.scopeType}|${resp.scopeKey}`} resp={resp} category={kpi?.category ?? ''} wrapRef={wrap} offset={offset} />
            )}
        </section>
    );
}

function EvidenceRows({ resp, category, wrapRef, offset }: { resp: EvidenceResponse; category: string; wrapRef: React.RefObject<HTMLDivElement>; offset: number | null }) {
    const [sort, setSort] = useState<EvidenceSort>({ key: null, direction: 'asc' });
    const [shown, setShown] = useState(EVIDENCE_PAGE);

    const columns = useMemo(
        () => orderColumns(resp.columns, resp.kpiKey, category).map(c => ({ key: c, width: WIDE.has(c) ? 220 : isEvidenceUrlColumn(c) ? 90 : 120 })),
        [resp.columns, resp.kpiKey, category],
    );
    const numeric = useMemo(() => numericEvidenceColumns(columns.map(c => c.key), resp.rows), [columns, resp.rows]);
    const sorted = useMemo(() => sortEvidenceRows(resp.rows, sort), [resp.rows, sort]);
    const visible = sorted.slice(0, shown);
    const rest = sorted.length - visible.length;
    const tableWidth = columns.reduce((s, c) => s + c.width, 0);
    const style = offset !== null ? ({ '--tbl-offset': `${offset}px` } as React.CSSProperties) : undefined;

    return (
        <>
            {rest > 0 && (
                <div className="toolbar" data-testid="skpi-evidence-pager">
                    <span className="count">{visible.length.toLocaleString('de-CH')} of {sorted.length.toLocaleString('de-CH')} rows shown; sorting covers all of them</span>
                    <button type="button" className="btn secondary sm" onClick={() => setShown(n => n + EVIDENCE_PAGE)}>
                        Show {Math.min(EVIDENCE_PAGE, rest).toLocaleString('de-CH')} more
                    </button>
                    <button type="button" className="btn quiet sm" onClick={() => setShown(sorted.length)}>Show all</button>
                </div>
            )}
            <div ref={wrapRef} className="tbl-wrap compact" style={style} role="region" aria-label="Evidence rows" tabIndex={0}>
                <table className="tbl tbl-fixed" style={{ width: tableWidth, minWidth: '100%' }}>
                    <colgroup>{columns.map(c => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
                    <thead>
                        <tr>
                            {columns.map(c => {
                                const active = sort.key === c.key;
                                return (
                                    <th
                                        key={c.key}
                                        scope="col"
                                        className={numeric.has(c.key) ? 'n' : undefined}
                                        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                    >
                                        <button type="button" className="sort" title={`${columnLabel(c.key)} (${c.key})`} onClick={() => setSort(s => nextEvidenceSort(s, c.key, numeric.has(c.key)))}>
                                            <span className="truncate">{columnLabel(c.key)}</span>
                                            {active && <span className="caret" aria-hidden="true">{sort.direction === 'asc' ? '▲' : '▼'}</span>}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((r, i) => (
                            <tr key={rowKey(r, i)}>
                                {columns.map(c => <Cell key={c.key} col={c.key} value={r[c.key]} numeric={numeric.has(c.key)} />)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function Cell({ col, value, numeric }: { col: string; value: unknown; numeric: boolean }) {
    const className = numeric ? 'n' : undefined;
    if (isEvidenceUrlColumn(col) && typeof value === 'string' && value !== '') {
        return (
            <td className={className}>
                <a className="lnk" href={value} target="_blank" rel="noreferrer">
                    HubSpot <ExternalLink className="i sm" aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
                </a>
            </td>
        );
    }
    const text = evidenceCellText(col, value);
    if (text === null) return <td className={className}><span className="dash">—</span></td>;
    return <td className={className} title={typeof value === 'string' ? value : undefined}>{text}</td>;
}

function rowKey(r: Record<string, unknown>, i: number): string {
    const id = r.DealId ?? r.EngagementId ?? r.Id ?? r.RecordId ?? null;
    return id !== null && id !== undefined ? `${String(id)}-${i}` : String(i);
}
